// Only type imports from `posthog-js` are allowed here: a value import would pull the SDK back into the main bundle
// The SDK lives in `posthogClient.ts` and is loaded lazily.
import type { PostHog } from 'posthog-js';

import { User } from '@/api/rest/resources/types/user';
import { Logger } from '@/lib/logs/logger';

import { AnalyticsEvent, POSTHOG_API_KEY, POSTHOG_BASE_URL } from '../constants';

let posthogInstance: PostHog | null = null;
let loadingPromise: Promise<void> | null = null;
let hasConsent = false;
let pendingCalls: Array<(posthog: PostHog) => void> = []; // calls made after consent was given but before the SDK chunk finished loading

const loadPostHog = () => {
  if (loadingPromise || !hasConsent) {
    return;
  }

  loadingPromise = import('./posthogClient')
    .then(({ createPostHogClient }) => {
      if (!hasConsent) {
        return;
      }

      posthogInstance = createPostHogClient();
      posthogInstance.opt_in_capturing();
      pendingCalls.forEach((call) => call(posthogInstance as PostHog));
    })
    .catch((error) => {
      Logger.error(error);
    })
    .finally(() => {
      pendingCalls = [];
      // allow loading again on the next consent change (after a failure or a revoked consent)
      if (!posthogInstance) {
        loadingPromise = null;
      }
    });
};

// cookiebot triggers "CookiebotOnAccept" event when user gives consent for "statistics"
// PostHog is only downloaded and initialized once we have user consent
export const setAnalyticsConsent = (consent: boolean) => {
  if (!POSTHOG_API_KEY || !POSTHOG_BASE_URL) {
    return;
  }

  // cookiebot can report the same consent several times (startup check, load and accept events).
  // Skip repeated calls so we don't emit duplicated "$opt_in" events, but retry a failed load.
  const isPostHogStarting = posthogInstance !== null || loadingPromise !== null;
  if (consent === hasConsent && (!consent || isPostHogStarting)) {
    return;
  }

  hasConsent = consent;

  if (!hasConsent) {
    pendingCalls = [];
    posthogInstance?.opt_out_capturing();
    return;
  }

  if (posthogInstance) {
    posthogInstance.opt_in_capturing();
  } else {
    loadPostHog();
  }
};

const runWithPostHog = (call: (posthog: PostHog) => void) => {
  // without consent PostHog is opted out, so events are dropped
  if (!hasConsent) return;

  if (posthogInstance) {
    call(posthogInstance);
  } else if (loadingPromise) {
    pendingCalls.push(call);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const captureEvent = ({ name, params }: { name: AnalyticsEvent; params?: Record<string, any> }) => {
  runWithPostHog((posthog) => posthog.capture(name, params));
};

export const identifyUser = (user: User, impersonatedBy?: string) => {
  runWithPostHog((posthog) =>
    posthog.identify(user.email, {
      id: user.id,
      email: user.email,
      firstname: user.firstname,
      lastname: user.lastname,
      is_staff: user.is_staff,
      impersonated_by: impersonatedBy,
      organization_id: user.membership?.organization.id ?? null,
      organization_name: user.membership?.organization.name ?? null,
      membership: user.membership,
    }),
  );
};
